import { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import CopyIcon from 'modules/v2/common/components/SvgIcon/CopyIcon';

import strings, { formatMessage } from '../../strings';
import { resolveCarrier, CARRIER } from '../../carrier';

const CARRIER_LABELS = {
  [CARRIER.UPS]: strings.detail.carrierUps,
  [CARRIER.FEDEX]: strings.detail.carrierFedex,
  [CARRIER.USPS]: strings.detail.carrierUsps,
};

/**
 * "Shipment reference" card on the member order detail (task 54/55).
 *
 * A MOD order mails one asset to N recipients via a bulk/batch method, so the
 * stored `shipping.trackingRef` is a single batch/shipment reference — NOT
 * per-recipient package tracking. This read-only card surfaces that reference
 * (copyable) plus a one-line note that recipients aren't tracked separately.
 * As a convenience, when the reference matches a carrier format (see
 * `carrier.js`) we still deep-link to the carrier's tracking page in a new tab;
 * a non-carrier batch/manifest id simply shows no link. Renders nothing when
 * there is no shipment reference.
 */
const TrackPackageCard = ({ trackingRef }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef();

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const ref = String(trackingRef ?? '').trim();

  if (!ref) {
    return null;
  }

  const { carrier, url } = resolveCarrier(ref);
  const carrierLabel = carrier ? CARRIER_LABELS[carrier] : null;

  const handleCopy = () => {
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="mb-6 flex max-w-xl flex-wrap items-center justify-between gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4"
      data-testid="mod-track-card"
    >
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          {carrierLabel
            ? formatMessage(strings.detail.trackCardCarrierLabel, { carrier: carrierLabel })
            : strings.detail.trackCardCarrierUnknownLabel}
        </div>
        <div className="mt-1 flex items-center gap-2">
          <span
            className="font-mono text-base font-semibold text-neutral-800"
            data-testid="mod-track-ref"
          >
            {ref}
          </span>
          <CopyToClipboard text={ref} onCopy={handleCopy}>
            <button
              type="button"
              aria-label={strings.detail.copyTrackingRef}
              data-testid="mod-track-copy"
              className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs font-semibold text-neutral-500 hover:bg-neutral-50"
            >
              <CopyIcon />
              {copied ? strings.detail.copied : strings.detail.copyTrackingRef}
            </button>
          </CopyToClipboard>
        </div>
        <p className="mt-2 max-w-md text-xs text-neutral-500" data-testid="mod-shipment-ref-note">
          {strings.detail.shipmentRefNote}
        </p>
        {!carrier && (
          <p className="mt-2 max-w-md text-xs text-neutral-500" data-testid="mod-track-unknown-note">
            {strings.detail.trackCardUnknownNote}
          </p>
        )}
      </div>

      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mod-track-link"
          aria-label={formatMessage(strings.detail.trackCardButtonAria, {
            carrier: carrierLabel,
          })}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-primary-600"
        >
          {strings.detail.trackCardButton}
          <span aria-hidden="true">↗</span>
        </a>
      )}
    </div>
  );
};

export default TrackPackageCard;
